/**
 * The workflow JSON <-> columns conversion, kept apart from the editor that
 * renders it.
 *
 * Pure and separately testable: these two functions define what the editor is
 * allowed to represent and what it writes back, which is the part that must
 * not drift from the backend's `StoredWorkflow`. Keeping them out of the
 * component file also keeps that file exporting components only, so fast
 * refresh works on it.
 */

/** One workflow column while it is being edited. Mirrors `StoredWorkflowColumn`. */
export interface WorkflowColumnDraft {
  name: string;
  statuses: string[];
}

/**
 * Parse the form's workflow JSON into columns, or `null` when it is not a
 * shape this editor can represent.
 *
 * `null` is not an error state to hide — it is what forces the JSON view,
 * which is the only place a malformed or unexpected workflow can be repaired.
 * Anything other than `{columns: [{name, statuses}]}` returns `null` rather
 * than being coerced, because silently dropping a key the backend understood
 * would delete configuration the user never asked to remove.
 */
export function parseWorkflowColumns(json: string): WorkflowColumnDraft[] | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const keys = Object.keys(parsed);
  if (keys.length !== 1 || keys[0] !== 'columns') return null;
  const columns = (parsed as { columns: unknown }).columns;
  if (!Array.isArray(columns)) return null;

  const drafts: WorkflowColumnDraft[] = [];
  for (const column of columns) {
    if (typeof column !== 'object' || column === null) return null;
    const columnKeys = Object.keys(column).sort();
    if (columnKeys.length !== 2 || columnKeys[0] !== 'name' || columnKeys[1] !== 'statuses') return null;
    const { name, statuses } = column as { name: unknown; statuses: unknown };
    if (typeof name !== 'string') return null;
    if (!Array.isArray(statuses) || statuses.some((s) => typeof s !== 'string')) return null;
    drafts.push({ name, statuses: statuses as string[] });
  }
  return drafts;
}

/**
 * Serialize columns back to the form's JSON field.
 *
 * Two-space indent and `{columns: […]}` exactly as the textarea held it, so
 * the payload `JSON.parse`d on save is identical to the one the textarea
 * produced for the same configuration — the editor changes how a workflow is
 * authored, never what is stored.
 */
export function serializeWorkflowColumns(columns: WorkflowColumnDraft[]): string {
  return JSON.stringify({ columns }, null, 2);
}
